import { FileEntryMode, FileUpload as IrisFileUpload, Flex } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { PreviewFileEntry } from "@/components/Files/preview/PreviewFileEntry";
import { InProgressFileEntry } from "@/components/Files/upload/InProgressFileEntry";
import { ATTACHMENTS_EXTENSIONS, buildDropzoneAccept, FileExtension } from "@/consts/fileExtensions";
import { Attachment, TAttachmentPurpose } from "@/data/attachments";
import { withFormikField } from "@/formikComponents/withFormikField";

import { FileUploadStateItem } from "./fileUploadReducer";
import { useSingleFileUpload } from "./useSingleFileUpload";

interface SingleFileUploadProps {
  attachment?: Attachment;
  onChange?: (value: FileUploadStateItem<Attachment>) => void;
  attachmentType?: TAttachmentPurpose;
  extensions?: FileExtension[];
  maxSize?: number;
  parsing?: boolean;
}

export const SingleFileUpload: FC<SingleFileUploadProps> = ({
  onChange,
  attachment,
  attachmentType,
  extensions = ATTACHMENTS_EXTENSIONS,
  maxSize = 50,
  parsing,
}) => {
  const { t } = useTranslation(["public", "common", "session"]);

  const { state, addDropzoneFiles, deleteFileEntry } = useSingleFileUpload({
    attachment,
    onChange,
    attachmentType,
    parsing,
  });

  const item = state.items[0];

  return (
    <Flex gap="xs" flexDirection="column">
      {!item ? (
        <IrisFileUpload
          onDrop={addDropzoneFiles}
          accept={buildDropzoneAccept(extensions)}
          maxSize={maxSize}
          header={t("session:COMPONENTS.DROPPABLE_FILE_UPLOAD.DRAG_AND_DROP_FILES")}
          link={t("session:COMPONENTS.DROPPABLE_FILE_UPLOAD.CHOOSE_FROM_DRIVE")}
        />
      ) : !item.attachment ? (
        <InProgressFileEntry
          attachment={item.file!}
          error={item.error && t(`common:VALIDATION_MESSAGES.${item.error}`, { size: maxSize })}
          progress={item.progress}
          onCancel={deleteFileEntry}
          onDelete={deleteFileEntry}
        />
      ) : (
        <PreviewFileEntry attachment={item.attachment!} mode={FileEntryMode.EDIT} onDelete={deleteFileEntry} />
      )}
    </Flex>
  );
};

export const FormikSingleFileUpload = withFormikField(SingleFileUpload);
